'use client'

import { useEffect, useRef } from 'react'
import { useRouter } from 'next/navigation'
import { useQueryClient } from '@tanstack/react-query'
import { authClient } from '@/lib/auth/client'

const CHANNEL_NAME = 'pv-auth-sync'

/**
 * Invisible component that keeps client-side state in sync with auth changes.
 *
 * Better-Auth replaces the legacy `onAuthStateChange` listener with a
 * polling `useSession()` hook. We watch its returned user id and:
 *
 * 1. When the local session id transitions present -> null we treat
 *    that as a sign-out, broadcast to other tabs, clear the React
 *    Query cache, and refresh the router so server components
 *    re-render.
 * 2. When a sign-out broadcast arrives from another tab we do the
 *    same cleanup locally so stale data does not linger.
 *
 * Sign-in / user-id swap intentionally does NOT trigger a refresh
 * here: real sign-ins happen through /login and land on the
 * destination page via a fresh navigation, so SSR has already
 * rendered with the right user. Refreshing here on top would
 * double-render.
 *
 * IMPORTANT - past regressions to remember:
 * - Reading session.user.id while `isPending` was true treated the
 *   transient `data: undefined` as "logged out" and fired the
 *   queryClient.clear() + router.refresh() pair. That made every
 *   feed post disappear and reload on reaction clicks (which race
 *   with the session refetch). Always bail on isPending.
 * - Better-Auth's useSession initially returns `{ data: undefined,
 *   isPending: false }` for one tick before kicking off the fetch.
 *   Seeding the previous-user-id ref on that first call recorded
 *   `null`; the eventual settle to `{ user: X }` then looked like
 *   a sign-in transition. We now skip seeding until we've observed
 *   at least one isPending=true cycle - that guarantees a real
 *   fetch produced the value we're recording.
 */
export function AuthSync() {
  const router = useRouter()
  const queryClient = useQueryClient()
  const { data: session, isPending } = authClient.useSession()
  const previousUserIdRef = useRef<string | null>(null)
  const initialisedRef = useRef(false)
  const sawPendingRef = useRef(false)

  useEffect(() => {
    if (isPending) {
      sawPendingRef.current = true
      return
    }

    // Skip the very first render where Better-Auth hands back
    // `{ data: undefined, isPending: false }` before its fetch starts.
    // Acting here would seed the ref with a fake `null` user-id and
    // misdiagnose the eventual settle as a sign-in.
    if (!sawPendingRef.current) return

    const currentUserId = session?.user?.id ?? null
    const previousUserId = previousUserIdRef.current

    // First real settled read: just seed the ref. Whatever the
    // session state is on arrival, treat it as the baseline.
    if (!initialisedRef.current) {
      initialisedRef.current = true
      previousUserIdRef.current = currentUserId
      return
    }

    if (previousUserId === currentUserId) return
    previousUserIdRef.current = currentUserId

    // Sign-out: broadcast and reset client state.
    if (previousUserId && !currentUserId) {
      try {
        const bc = new BroadcastChannel(CHANNEL_NAME)
        bc.postMessage({ type: 'SIGNED_OUT' })
        bc.close()
      } catch {
        // BroadcastChannel not supported - graceful degradation
      }
      queryClient.clear()
      router.refresh()
    }

    // Sign-in / user-id swap is intentionally a no-op here - see the
    // file-level comment.
  }, [isPending, session?.user?.id, queryClient, router])

  // Listen for sign-out broadcasts from other tabs.
  useEffect(() => {
    let bc: BroadcastChannel | null = null
    try {
      bc = new BroadcastChannel(CHANNEL_NAME)
      bc.onmessage = (event) => {
        if (event.data?.type === 'SIGNED_OUT') {
          queryClient.clear()
          router.refresh()
        }
      }
    } catch {
      // BroadcastChannel not supported
    }
    return () => {
      bc?.close()
    }
  }, [queryClient, router])

  return null
}
